import { Decision } from '../types/game';

export const decisions: Decision[] = [
  // Early Adulthood Decisions
  {
    id: 'decision-zus',
    title: 'ZUS Insurance Registration',
    description: 'Decide whether to register for ZUS (Social Insurance Institution) insurance',
    category: 'finance',
    stage: 'early-adulthood',
    minAge: 18,
    maxAge: 20,
    mandatory: false,
    icon: '🏛️',
    options: [
      {
        id: 'register-zus',
        title: 'Register for ZUS',
        description: 'Register for ZUS insurance - provides social security benefits but requires monthly contributions',
        statChanges: { wealth: -15, happiness: 10, health: 5 },
        cost: 0, // No upfront cost, but monthly contributions
        nextDecisions: ['decision-1', 'decision-2'],
      },
      {
        id: 'skip-zus',
        title: 'Skip ZUS Registration',
        description: 'Choose not to register for ZUS insurance - save money but lose social security benefits',
        statChanges: { wealth: 10, happiness: -5, health: -5 },
        cost: 0,
        nextDecisions: ['decision-1', 'decision-2'],
      },
    ],
  },
  {
    id: 'decision-1',
    title: 'Higher Education Path',
    description: 'Choose your educational direction after high school',
    category: 'education',
    stage: 'early-adulthood',
    minAge: 18,
    maxAge: 20,
    mandatory: true,
    icon: '🎓',
    options: [
      {
        id: 'university',
        title: 'University Degree',
        description: 'Pursue a 4-year university degree',
        statChanges: { education: 20, wealth: -30, happiness: 5 },
        cost: 15000,
        nextDecisions: ['decision-4', 'decision-5'],
        debtWarning: 'This decision will put you in debt if you cannot afford it. Consider taking out student loans.',
      },
      {
        id: 'trade-school',
        title: 'Trade School',
        description: 'Learn a skilled trade',
        statChanges: { education: 10, wealth: -10, career: 15 },
        cost: 8000,
        nextDecisions: ['decision-6'],
        debtWarning: 'This decision may put you in debt if you cannot afford it.',
      },
      {
        id: 'work-directly',
        title: 'Enter Workforce',
        description: 'Start working immediately',
        statChanges: { wealth: 20, career: 5, happiness: -5 },
        nextDecisions: ['decision-7'],
      },
      {
        id: 'quit-job',
        title: 'Quit Job',
        description: 'Leave your current job to focus on other priorities',
        statChanges: { wealth: -10, happiness: 10, career: -5 },
        cost: 0,
      },
    ],
  },
  {
    id: 'decision-2',
    title: 'Living Arrangement',
    description: 'Decide where to live after high school',
    category: 'lifestyle',
    stage: 'early-adulthood',
    minAge: 18,
    maxAge: 22,
    icon: '🏠',
    options: [
      {
        id: 'dormitory',
        title: 'University Dormitory',
        description: 'Live in campus housing',
        statChanges: { relationships: 15, wealth: -20, happiness: 10 },
        cost: 6000,
        nextDecisions: ['decision-3'],
        debtWarning: 'This decision may put you in debt if you cannot afford it.',
      },
      {
        id: 'apartment',
        title: 'Rent Apartment',
        description: 'Rent your own place',
        statChanges: { wealth: -25, happiness: 5 },
        cost: 8000,
        nextDecisions: ['decision-3'],
        debtWarning: 'This decision may put you in debt if you cannot afford it.',
      },
      {
        id: 'parents',
        title: 'Stay with Parents',
        description: 'Continue living at home',
        statChanges: { wealth: 10, relationships: 5 },
      },
    ],
  },
  {
    id: 'decision-3',
    title: 'Health & Fitness',
    description: 'Establish your health and fitness routine',
    category: 'health',
    stage: 'early-adulthood',
    minAge: 19,
    maxAge: 23,
    icon: '💪',
    options: [
      {
        id: 'gym-membership',
        title: 'Gym Membership',
        description: 'Join a fitness center',
        statChanges: { health: 15, wealth: -10, happiness: 5 },
        cost: 5000,
      },
      {
        id: 'home-workout',
        title: 'Home Workouts',
        description: 'Exercise at home',
        statChanges: { health: 10, wealth: 5 },
      },
      {
        id: 'sports-team',
        title: 'Join Sports Team',
        description: 'Participate in team sports',
        statChanges: { health: 12, relationships: 10, happiness: 8 },
      },
    ],
  },

  // Young Adult Decisions
  {
    id: 'decision-relationship-1',
    title: 'Finding Love',
    description: 'You\'ve met someone special and feel a strong connection. What do you do?',
    category: 'relationships',
    stage: 'young-adult',
    minAge: 25,
    maxAge: 25,
    mandatory: false,
    icon: '💕',
    options: [
      {
        id: 'pursue-relationship',
        title: 'Pursue the Relationship',
        description: 'Ask them out and see where it leads',
        statChanges: { relationships: 25, happiness: 15, wealth: -5 },
        cost: 1000, // Dating expenses
        nextDecisions: ['decision-relationship-2'],
      },
      {
        id: 'stay-friends',
        title: 'Stay Friends',
        description: 'Keep the relationship platonic for now',
        statChanges: { relationships: 10, happiness: 5 },
        cost: 0,
        nextDecisions: ['decision-relationship-3'],
      },
      {
        id: 'focus-career',
        title: 'Focus on Career',
        description: 'Prioritize work over relationships right now',
        statChanges: { career: 15, relationships: -5, happiness: -5 },
        cost: 0,
        nextDecisions: ['decision-career-focus'],
      },
    ],
  },
  {
    id: 'decision-relationship-2',
    title: 'Serious Relationship',
    description: 'Your relationship is getting serious. What\'s your next step?',
    category: 'relationships',
    stage: 'young-adult',
    minAge: 30,
    maxAge: 30,
    mandatory: false,
    icon: '💍',
    options: [
      {
        id: 'move-in-together',
        title: 'Move In Together',
        description: 'Take the next step and live together',
        statChanges: { relationships: 30, happiness: 20, wealth: -10 },
        cost: 2500, // Moving costs
        nextDecisions: ['decision-relationship-4'],
      },
      {
        id: 'get-engaged',
        title: 'Get Engaged',
        description: 'Propose and plan for marriage',
        statChanges: { relationships: 35, happiness: 25, wealth: -20 },
        cost: 5000, // Engagement ring and planning
        nextDecisions: ['decision-marriage'],
      },
      {
        id: 'take-it-slow',
        title: 'Take It Slow',
        description: 'Continue dating without rushing into anything',
        statChanges: { relationships: 15, happiness: 10 },
        cost: 2000, // Continued dating expenses
        nextDecisions: ['decision-relationship-3'],
      },
    ],
  },
  {
    id: 'decision-4',
    title: 'Career Specialization',
    description: 'Choose your career focus after university',
    category: 'career',
    stage: 'young-adult',
    minAge: 23,
    maxAge: 25,
    prerequisites: ['decision-1'],
    icon: '💼',
    options: [
      {
        id: 'tech-industry',
        title: 'Technology Sector',
        description: 'Work in software, AI, or tech startups',
        statChanges: { career: 25, wealth: 20, health: -5 },
      },
      {
        id: 'finance',
        title: 'Financial Services',
        description: 'Work in banking, investment, or consulting',
        statChanges: { career: 20, wealth: 25, happiness: -10 },
      },
      {
        id: 'healthcare',
        title: 'Healthcare',
        description: 'Work in medical or healthcare fields',
        statChanges: { career: 18, health: 10, relationships: 5 },
      },
      {
        id: 'education',
        title: 'Education',
        description: 'Work in teaching or educational administration',
        statChanges: { career: 15, relationships: 15, wealth: 5 },
      },
    ],
  },


  {
    id: 'decision-6',
    title: 'Financial Planning',
    description: 'Establish your approach to money management',
    category: 'finance',
    stage: 'young-adult',
    minAge: 45,
    maxAge: 45,
    icon: '💰',
    options: [
      {
        id: 'aggressive-saving',
        title: 'Aggressive Saving',
        description: 'Save 30% of income for future goals',
        statChanges: { wealth: 25, happiness: -5, health: -5 },
      },
      {
        id: 'balanced-approach',
        title: 'Balanced Approach',
        description: 'Save 15% while enjoying life',
        statChanges: { wealth: 15, happiness: 5 },
      },
      {
        id: 'invest-education',
        title: 'Invest in Education',
        description: 'Use money for further learning',
        statChanges: { education: 20, wealth: -10, career: 10 },
        cost: 5000,
      },
      {
        id: 'invest-stocks',
        title: 'Invest in Stocks',
        description: 'Put money into the stock market',
        statChanges: { wealth: 15, happiness: 5 },
        cost: 10000,
      },
      {
        id: 'invest-real-estate',
        title: 'Invest in Real Estate',
        description: 'Buy property as an investment',
        statChanges: { wealth: 25, happiness: 10 },
        cost: 50000,
      },
    ],
  },

  // Middle Age Decisions
  {
    id: 'decision-marriage',
    title: 'Marriage Decision',
    description: 'You\'re engaged! How do you want to handle the wedding?',
    category: 'relationships',
    stage: 'young-adult',
    minAge: 35,
    maxAge: 35,
    mandatory: false,
    icon: '💒',
    options: [
      {
        id: 'big-wedding',
        title: 'Big Wedding',
        description: 'Plan a large, traditional wedding ceremony',
        statChanges: { relationships: 40, happiness: 30, wealth: -50 },
        cost: 15000,
        nextDecisions: [],
      },
      {
        id: 'small-wedding',
        title: 'Small Wedding',
        description: 'Have an intimate ceremony with close family and friends',
        statChanges: { relationships: 35, happiness: 25, wealth: -15 },
        cost: 6000,
        nextDecisions: [],
      },
      {
        id: 'elopement',
        title: 'Elope',
        description: 'Get married privately without a big ceremony',
        statChanges: { relationships: 30, happiness: 20, wealth: -5 },
        cost: 2000,
        nextDecisions: [],
      },
    ],
  },
  {
    id: 'decision-8',
    title: 'Career Advancement',
    description: 'Choose your path for career growth',
    category: 'career',
    stage: 'middle-age',
    icon: '📈',
    options: [
      {
        id: 'management-track',
        title: 'Management Track',
        description: 'Move into leadership roles',
        statChanges: { career: 20, wealth: 25, health: -3, relationships: -5 },
      },
      {
        id: 'expert-track',
        title: 'Expert Track',
        description: 'Become a specialist in your field',
        statChanges: { career: 15, wealth: 15, happiness: 10 },
      },
      {
        id: 'entrepreneurship',
        title: 'Start Business',
        description: 'Launch your own company',
        statChanges: { career: 30, wealth: 10, health: -5, relationships: -10 },
      },
    ],
  },
  {
    id: 'decision-9',
    title: 'Health Investment',
    description: 'Invest in your long-term health',
    category: 'health',
    stage: 'middle-age',
    icon: '🏥',
    options: [
      {
        id: 'premium-healthcare',
        title: 'Premium Healthcare',
        description: 'Invest in comprehensive health coverage',
        statChanges: { health: 20, wealth: -15 },
        cost: 20000,
      },
      {
        id: 'wellness-program',
        title: 'Wellness Program',
        description: 'Join a comprehensive wellness program',
        statChanges: { health: 15, happiness: 10, wealth: -10 },
        cost: 12000,
      },
      {
        id: 'preventive-care',
        title: 'Preventive Care',
        description: 'Focus on regular checkups and prevention',
        statChanges: { health: 10, wealth: -5 },
        cost: 5000,
      },
    ],
  },

  // Senior Decisions
  {
    id: 'decision-10',
    title: 'Retirement Planning',
    description: 'Plan for your retirement years',
    category: 'finance',
    stage: 'senior',
    icon: '🏖️',
    options: [
      {
        id: 'early-retirement',
        title: 'Early Retirement',
        description: 'Retire early and enjoy life',
        statChanges: { happiness: 25, wealth: -20, health: 5 },
      },
      {
        id: 'gradual-retirement',
        title: 'Gradual Retirement',
        description: 'Reduce work hours gradually',
        statChanges: { happiness: 15, wealth: 5, health: 10 },
      },
      {
        id: 'continue-working',
        title: 'Continue Working',
        description: 'Keep working as long as possible',
        statChanges: { wealth: 15, health: -5, happiness: -5 },
      },
    ],
  },

  // Additional Young Adult Decisions
  {
    id: 'decision-11',
    title: 'Career Change',
    description: 'Consider changing your career path',
    category: 'career',
    stage: 'young-adult',
    minAge: 60,
    maxAge: 62,
    icon: '🔄',
    prerequisites: ['decision-4'],
    options: [
      {
        id: 'switch-industry',
        title: 'Switch Industry',
        description: 'Move to a different industry',
        statChanges: { career: 10, wealth: -10, happiness: 5 },
        cost: 5000,
      },
      {
        id: 'start-freelancing',
        title: 'Start Freelancing',
        description: 'Become a freelancer',
        statChanges: { career: 15, wealth: 5, happiness: 10 },
      },
      {
        id: 'stay-current',
        title: 'Stay Current',
        description: 'Continue in your current field',
        statChanges: { career: 5, wealth: 10 },
      },
    ],
  },
  {
    id: 'decision-12',
    title: 'Health Crisis',
    description: 'You face a health challenge',
    category: 'health',
    stage: 'young-adult',
    minAge: 65,
    maxAge: 67,
    icon: '🏥',
    mandatory: true,
    options: [
      {
        id: 'seek-treatment',
        title: 'Seek Medical Treatment',
        description: 'Get professional medical help',
        statChanges: { health: 20, wealth: -15, happiness: 5 },
        cost: 10000,
      },
      {
        id: 'alternative-medicine',
        title: 'Alternative Medicine',
        description: 'Try alternative treatments',
        statChanges: { health: 10, wealth: -5, happiness: 10 },
        cost: 3000,
      },
      {
        id: 'ignore-problem',
        title: 'Ignore the Problem',
        description: 'Hope it goes away on its own',
        statChanges: { health: -5, wealth: 5, happiness: -10 },
      },
    ],
  },
  {
    id: 'decision-13',
    title: 'Investment Opportunity',
    description: 'A friend offers you an investment opportunity',
    category: 'finance',
    stage: 'young-adult',
    minAge: 70,
    maxAge: 72,
    icon: '💎',
    options: [
      {
        id: 'invest-heavily',
        title: 'Invest Heavily',
        description: 'Put significant money into the opportunity',
        statChanges: { wealth: 30, happiness: 10 },
        cost: 20000,
      },
      {
        id: 'invest-moderately',
        title: 'Invest Moderately',
        description: 'Put some money into the opportunity',
        statChanges: { wealth: 15, happiness: 5 },
        cost: 5000,
      },
      {
        id: 'decline-investment',
        title: 'Decline Investment',
        description: 'Pass on the opportunity',
        statChanges: { wealth: 5, happiness: -5 },
      },
    ],
  },

  // Middle Age Additional Decisions
  {
    id: 'decision-14',
    title: 'Midlife Crisis',
    description: 'You experience a midlife crisis',
    category: 'lifestyle',
    stage: 'middle-age',
    icon: '🌪️',
    mandatory: true,
    options: [
      {
        id: 'buy-sports-car',
        title: 'Buy Sports Car',
        description: 'Splurge on an expensive car',
        statChanges: { happiness: 20, wealth: -25, health: 5 },
        cost: 50000,
      },
      {
        id: 'travel-world',
        title: 'Travel the World',
        description: 'Take a year off to travel',
        statChanges: { happiness: 25, wealth: -20, health: 10 },
        cost: 30000,
      },
      {
        id: 'focus-family',
        title: 'Focus on Family',
        description: 'Spend more time with loved ones',
        statChanges: { happiness: 15, relationships: 20, wealth: 5 },
      },
    ],
  },
  {
    id: 'decision-15',
    title: 'Career Plateau',
    description: 'You hit a career plateau',
    category: 'career',
    stage: 'middle-age',
    icon: '📊',
    options: [
      {
        id: 'pursue-promotion',
        title: 'Pursue Promotion',
        description: 'Work harder for advancement',
        statChanges: { career: 20, wealth: 15, health: -3, happiness: -5 },
      },
      {
        id: 'start-business',
        title: 'Start Your Own Business',
        description: 'Launch your own company',
        statChanges: { career: 25, wealth: 10, health: -5, happiness: 10 },
        cost: 25000,
      },
      {
        id: 'accept-plateau',
        title: 'Accept the Plateau',
        description: 'Focus on work-life balance',
        statChanges: { career: 5, health: 10, happiness: 15 },
      },
    ],
  },
  {
    id: 'decision-16',
    title: 'Health Scare',
    description: 'You receive concerning health news',
    category: 'health',
    stage: 'middle-age',
    icon: '⚠️',
    mandatory: true,
    options: [
      {
        id: 'aggressive-treatment',
        title: 'Aggressive Treatment',
        description: 'Pursue all available treatments',
        statChanges: { health: 25, wealth: -30, happiness: 5 },
        cost: 50000,
      },
      {
        id: 'lifestyle-changes',
        title: 'Lifestyle Changes',
        description: 'Make major lifestyle improvements',
        statChanges: { health: 20, wealth: -10, happiness: 10 },
        cost: 15000,
      },
      {
        id: 'wait-and-see',
        title: 'Wait and See',
        description: 'Monitor the situation',
        statChanges: { health: 5, wealth: 5, happiness: -5 },
      },
    ],
  },

  // Senior Additional Decisions
  {
    id: 'decision-17',
    title: 'Grandchildren',
    description: 'Your children have children',
    category: 'relationships',
    stage: 'senior',
    icon: '👶',
    prerequisites: ['decision-7'],
    options: [
      {
        id: 'active-grandparent',
        title: 'Active Grandparent',
        description: 'Be very involved in their lives',
        statChanges: { relationships: 25, happiness: 20, health: 5, wealth: -10 },
      },
      {
        id: 'occasional-visits',
        title: 'Occasional Visits',
        description: 'Visit when convenient',
        statChanges: { relationships: 15, happiness: 10, wealth: 5 },
      },
      {
        id: 'distant-relationship',
        title: 'Distant Relationship',
        description: 'Keep your distance',
        statChanges: { relationships: 5, happiness: 5, wealth: 10 },
      },
    ],
  },
  {
    id: 'decision-18',
    title: 'Estate Planning',
    description: 'Plan for your legacy',
    category: 'finance',
    stage: 'senior',
    icon: '📜',
    options: [
      {
        id: 'comprehensive-will',
        title: 'Comprehensive Will',
        description: 'Create detailed estate plans',
        statChanges: { wealth: 10, happiness: 15, relationships: 10 },
        cost: 10000,
      },
      {
        id: 'simple-will',
        title: 'Simple Will',
        description: 'Basic estate planning',
        statChanges: { wealth: 5, happiness: 10 },
        cost: 3000,
      },
      {
        id: 'no-planning',
        title: 'No Planning',
        description: 'Let family handle it',
        statChanges: { wealth: 5, happiness: -5, relationships: -10 },
      },
    ],
  },

  // Random Life Events (can appear at any stage)
  {
    id: 'decision-19',
    title: 'Lottery Win',
    description: 'You win a small lottery prize',
    category: 'finance',
    stage: 'young-adult',
    minAge: 18,
    maxAge: 50,
    icon: '🎰',
    options: [
      {
        id: 'save-winnings',
        title: 'Save Winnings',
        description: 'Invest the money wisely',
        statChanges: { wealth: 20, happiness: 10 },
      },
      {
        id: 'spend-winnings',
        title: 'Spend Winnings',
        description: 'Use the money for immediate needs',
        statChanges: { wealth: 10, happiness: 15, health: 5 },
      },
      {
        id: 'donate-winnings',
        title: 'Donate Winnings',
        description: 'Give the money to charity',
        statChanges: { wealth: 5, happiness: 20, relationships: 10 },
      },
    ],
  },
  {
    id: 'decision-20',
    title: 'Job Offer',
    description: 'You receive an unexpected job offer',
    category: 'career',
    stage: 'young-adult',
    minAge: 25,
    maxAge: 35,
    icon: '💼',
    options: [
      {
        id: 'accept-offer',
        title: 'Accept Offer',
        description: 'Take the new job',
        statChanges: { career: 15, wealth: 20, happiness: 10 },
      },
      {
        id: 'negotiate-offer',
        title: 'Negotiate Offer',
        description: 'Try to get better terms',
        statChanges: { career: 10, wealth: 15, happiness: 5 },
      },
      {
        id: 'decline-offer',
        title: 'Decline Offer',
        description: 'Stay in current position',
        statChanges: { career: 5, wealth: 5, happiness: -5 },
      },
    ],
  },

  // Young Adult Decisions (20-30)
  {
    id: 'decision-pet-adoption',
    title: 'Pet Adoption Decision',
    description: 'You found a stray animal. Should you adopt it?',
    category: 'lifestyle',
    stage: 'young-adult',
    minAge: 28,
    maxAge: 30,
    mandatory: false,
    icon: '🐕',
    options: [
      {
        id: 'adopt-pet',
        title: 'Adopt the Pet',
        description: 'Take the animal home and give it a loving family',
        statChanges: { happiness: 20, relationships: 15, health: 5 },
        cost: 200, // Initial adoption costs
        nextDecisions: ['decision-pet-care'],
      },
      {
        id: 'take-to-shelter',
        title: 'Take to Animal Shelter',
        description: 'Drop it off at the local animal shelter',
        statChanges: { happiness: 5, relationships: 5 },
        cost: 50, // Transportation costs
      },
      {
        id: 'ignore-pet',
        title: 'Ignore the Animal',
        description: 'Walk away and hope someone else helps',
        statChanges: { happiness: -5 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-pet-care',
    title: 'Pet Care Commitment',
    description: 'Your pet needs ongoing care. How will you handle the responsibilities?',
    category: 'lifestyle',
    stage: 'young-adult',
    minAge: 29,
    maxAge: 31,
    mandatory: false,
    icon: '🐾',
    options: [
      {
        id: 'dedicated-care',
        title: 'Dedicated Pet Care',
        description: 'Invest in premium pet care and training',
        statChanges: { happiness: 15, relationships: 10, health: 5 },
        cost: 100, // Monthly pet expenses
      },
      {
        id: 'basic-care',
        title: 'Basic Pet Care',
        description: 'Provide essential care without extra expenses',
        statChanges: { happiness: 10, relationships: 5 },
        cost: 50, // Monthly pet expenses
      },
      {
        id: 'rehome-pet',
        title: 'Rehome the Pet',
        description: 'Find a new home for the pet',
        statChanges: { happiness: -10, relationships: -5 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-startup-invitation',
    title: 'Startup Co-founder Invitation',
    description: 'Your friend wants you to join their startup as a co-founder. This is a major life decision.',
    category: 'career',
    stage: 'young-adult',
    minAge: 32,
    maxAge: 34,
    mandatory: false,
    icon: '🚀',
    options: [
      {
        id: 'join-startup',
        title: 'Join the Startup',
        description: 'Take the risk and become a co-founder',
        statChanges: { career: 20, wealth: -25, happiness: 15 },
        cost: 15000, // Initial investment
        debtWarning: 'This decision will put you in debt if you cannot afford it. Starting a business is risky but potentially very rewarding.',
        nextDecisions: ['decision-startup-strategy'],
      },
      {
        id: 'decline-startup',
        title: 'Stay at Current Job',
        description: 'Play it safe and keep your stable job',
        statChanges: { career: 5, happiness: -5 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-startup-strategy',
    title: 'Startup Business Strategy',
    description: 'How will you approach the startup business strategy?',
    category: 'career',
    stage: 'young-adult',
    mandatory: false,
    icon: '📊',
    options: [
      {
        id: 'aggressive-growth',
        title: 'Aggressive Growth Strategy',
        description: 'Focus on rapid expansion and market capture',
        statChanges: { career: 25, wealth: -30, happiness: 10 },
        cost: 25000, // Additional investment
        debtWarning: 'This aggressive approach requires significant investment and may lead to debt.',
      },
      {
        id: 'conservative-approach',
        title: 'Conservative Approach',
        description: 'Focus on steady, sustainable growth',
        statChanges: { career: 15, wealth: -10, happiness: 15 },
        cost: 5000, // Minimal additional investment
      },
      {
        id: 'part-time-startup',
        title: 'Part-time Startup',
        description: 'Keep your day job while working on startup evenings/weekends',
        statChanges: { career: 10, wealth: -5, happiness: 5 },
        cost: 2000,
      },
    ],
  },

  // Middle Age Decisions (30-50)
  {
    id: 'decision-midlife-crisis',
    title: 'Midlife Crisis Response',
    description: 'You\'re experiencing a midlife crisis. How will you respond?',
    category: 'lifestyle',
    stage: 'middle-age',
    minAge: 75,
    maxAge: 77,
    mandatory: false,
    icon: '🌅',
    options: [
      {
        id: 'buy-sports-car',
        title: 'Buy a Sports Car',
        description: 'Splurge on an expensive sports car to feel young again',
        statChanges: { happiness: 20, wealth: -30, relationships: 5 },
        cost: 50000,
        debtWarning: 'This expensive purchase may put you in significant debt.',
      },
      {
        id: 'travel-the-world',
        title: 'Travel the World',
        description: 'Take a sabbatical and travel to find yourself',
        statChanges: { happiness: 25, wealth: -20, relationships: 10 },
        cost: 30000,
        debtWarning: 'Extended travel requires significant financial investment.',
      },
      {
        id: 'therapy-counseling',
        title: 'Seek Therapy/Counseling',
        description: 'Work through your feelings with professional help',
        statChanges: { happiness: 15, health: 10, wealth: -5 },
        cost: 5000,
      },
      {
        id: 'ignore-crisis',
        title: 'Ignore the Feelings',
        description: 'Push through and continue with your current life',
        statChanges: { happiness: -10, health: -5 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-investment-opportunity',
    title: 'Major Investment Opportunity',
    description: 'A friend offers you a chance to invest in a promising real estate project.',
    category: 'finance',
    stage: 'middle-age',
    minAge: 80,
    maxAge: 82,
    mandatory: false,
    icon: '🏘️',
    options: [
      {
        id: 'invest-heavily',
        title: 'Invest Heavily',
        description: 'Put a large amount into the real estate project',
        statChanges: { wealth: 30, happiness: 10 },
        cost: 100000,
        debtWarning: 'This large investment may require taking on significant debt.',
      },
      {
        id: 'invest-moderately',
        title: 'Invest Moderately',
        description: 'Make a reasonable investment in the project',
        statChanges: { wealth: 15, happiness: 5 },
        cost: 30000,
        debtWarning: 'This investment may put you in debt if you cannot afford it.',
      },
      {
        id: 'invest-small',
        title: 'Small Investment',
        description: 'Make a small, safe investment',
        statChanges: { wealth: 5, happiness: 2 },
        cost: 5000,
      },
      {
        id: 'decline-investment',
        title: 'Decline Investment',
        description: 'Pass on this opportunity',
        statChanges: { wealth: 0, happiness: -2 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-health-scare',
    title: 'Health Scare Response',
    description: 'You\'ve received concerning health test results. How will you respond?',
    category: 'health',
    stage: 'middle-age',
    minAge: 85,
    maxAge: 87,
    mandatory: false,
    icon: '🏥',
    options: [
      {
        id: 'comprehensive-treatment',
        title: 'Comprehensive Treatment',
        description: 'Seek the best medical care available, regardless of cost',
        statChanges: { health: 25, wealth: -20, happiness: 10 },
        cost: 50000,
        debtWarning: 'Comprehensive medical treatment may require significant debt.',
      },
      {
        id: 'standard-treatment',
        title: 'Standard Treatment',
        description: 'Follow standard medical recommendations',
        statChanges: { health: 15, wealth: -10, happiness: 5 },
        cost: 15000,
        debtWarning: 'Medical treatment may put you in debt if you cannot afford it.',
      },
      {
        id: 'alternative-medicine',
        title: 'Alternative Medicine',
        description: 'Try alternative and holistic treatments',
        statChanges: { health: 10, wealth: -5, happiness: 5 },
        cost: 5000,
      },
      {
        id: 'ignore-health',
        title: 'Ignore the Results',
        description: 'Hope the problem goes away on its own',
        statChanges: { health: -20, happiness: -10 },
        cost: 0,
      },
    ],
  },

  // Senior Decisions (50+)
  {
    id: 'decision-retirement-planning',
    title: 'Retirement Planning Strategy',
    description: 'It\'s time to seriously plan for retirement. What approach will you take?',
    category: 'finance',
    stage: 'senior',
    mandatory: false,
    icon: '🏖️',
    options: [
      {
        id: 'aggressive-savings',
        title: 'Aggressive Savings',
        description: 'Maximize retirement contributions and cut expenses',
        statChanges: { wealth: 20, happiness: -5, health: 5 },
        cost: 0, // No upfront cost, but reduced current spending
      },
      {
        id: 'investment-heavy',
        title: 'Heavy Investment Strategy',
        description: 'Invest heavily in stocks and real estate for retirement',
        statChanges: { wealth: 25, happiness: 5 },
        cost: 50000,
        debtWarning: 'Heavy investment may require taking on debt.',
      },
      {
        id: 'conservative-approach',
        title: 'Conservative Approach',
        description: 'Focus on safe, low-risk retirement savings',
        statChanges: { wealth: 10, happiness: 10, health: 5 },
        cost: 10000,
      },
      {
        id: 'ignore-retirement',
        title: 'Ignore Retirement Planning',
        description: 'Continue living for today without planning for retirement',
        statChanges: { wealth: -10, happiness: 5 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-family-crisis',
    title: 'Family Crisis Response',
    description: 'A family member is in serious trouble and needs your help.',
    category: 'social',
    stage: 'middle-age',
    mandatory: false,
    icon: '👨‍👩‍👧‍👦',
    options: [
      {
        id: 'help-financially',
        title: 'Provide Financial Help',
        description: 'Give them the money they need to solve their problem',
        statChanges: { relationships: 25, wealth: -20, happiness: 10 },
        cost: 25000,
        debtWarning: 'Providing financial help may put you in debt.',
      },
      {
        id: 'help-emotionally',
        title: 'Provide Emotional Support',
        description: 'Be there for them emotionally without financial help',
        statChanges: { relationships: 15, happiness: 5 },
        cost: 0,
      },
      {
        id: 'help-practically',
        title: 'Provide Practical Help',
        description: 'Help them find solutions and resources',
        statChanges: { relationships: 10, happiness: 5 },
        cost: 2000, // Time and small expenses
      },
      {
        id: 'distance-self',
        title: 'Distance Yourself',
        description: 'Protect yourself from their problems',
        statChanges: { relationships: -15, happiness: -5 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-career-change',
    title: 'Major Career Change',
    description: 'You have an opportunity to completely change your career path.',
    category: 'career',
    stage: 'middle-age',
    mandatory: false,
    icon: '🔄',
    options: [
      {
        id: 'pursue-passion',
        title: 'Pursue Your Passion',
        description: 'Follow your dreams even if it means starting over',
        statChanges: { happiness: 20, career: -10, wealth: -15 },
        cost: 20000,
        debtWarning: 'Starting a new career may require significant financial investment.',
      },
      {
        id: 'lateral-move',
        title: 'Lateral Career Move',
        description: 'Make a lateral move to a different industry',
        statChanges: { happiness: 10, career: 5, wealth: -5 },
        cost: 5000,
      },
      {
        id: 'stay-current',
        title: 'Stay in Current Career',
        description: 'Continue with your current career path',
        statChanges: { happiness: -5, career: 5, wealth: 5 },
        cost: 0,
      },
    ],
  },

  // Additional Life Decisions
  {
    id: 'decision-street-cat',
    title: 'Street Cat Found',
    description: 'You found a stray cat on the street. It looks hungry and lost.',
    category: 'lifestyle',
    stage: 'young-adult',
    minAge: 26,
    maxAge: 28,
    mandatory: false,
    icon: '🐱',
    options: [
      {
        id: 'adopt-cat',
        title: 'Adopt the Cat',
        description: 'Take the cat home and give it a loving family',
        statChanges: { happiness: 15, relationships: 10, health: 5 },
        cost: 150, // Initial adoption costs
      },
      {
        id: 'feed-cat',
        title: 'Just Feed the Cat',
        description: 'Give it some food but don\'t take it home',
        statChanges: { happiness: 5, relationships: 3 },
        cost: 10, // Food cost
      },
      {
        id: 'ignore-cat',
        title: 'Ignore the Cat',
        description: 'Walk away and hope someone else helps',
        statChanges: { happiness: -3 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-friend-help',
    title: 'Friend in Need',
    description: 'Your friend is going through a difficult time and needs your help.',
    category: 'social',
    stage: 'young-adult',
    minAge: 29,
    maxAge: 31,
    mandatory: false,
    icon: '🤝',
    options: [
      {
        id: 'help-friend',
        title: 'Help Your Friend',
        description: 'Be there for them and provide support',
        statChanges: { relationships: 20, happiness: 10, wealth: -5 },
        cost: 500, // Time and small expenses
      },
      {
        id: 'help-financially',
        title: 'Provide Financial Help',
        description: 'Give them money to help with their situation',
        statChanges: { relationships: 25, happiness: 15, wealth: -15 },
        cost: 2000,
        debtWarning: 'Providing financial help may put you in debt.',
      },
      {
        id: 'decline-help',
        title: 'Cannot Help Right Now',
        description: 'Explain you have your own problems to deal with',
        statChanges: { relationships: -10, happiness: -5 },
        cost: 0,
      },
    ],
  },

  // Career Specialization Decision (Later in Life)
  {
    id: 'decision-career-specialization',
    title: 'Career Specialization',
    description: 'You have the opportunity to specialize in a specific area of your field. This could significantly boost your career.',
    category: 'career',
    stage: 'middle-age',
    minAge: 35,
    maxAge: 37,
    mandatory: false,
    icon: '🎯',
    options: [
      {
        id: 'technical-specialization',
        title: 'Technical Specialization',
        description: 'Focus on becoming a technical expert in your field',
        statChanges: { career: 25, wealth: 15, happiness: 10 },
        cost: 5000, // Training and certification costs
      },
      {
        id: 'management-specialization',
        title: 'Management Specialization',
        description: 'Develop leadership and management skills',
        statChanges: { career: 20, wealth: 20, happiness: 5 },
        cost: 8000, // Management training costs
        debtWarning: 'Management training may put you in debt if you cannot afford it.',
      },
      {
        id: 'entrepreneurial-specialization',
        title: 'Entrepreneurial Focus',
        description: 'Learn business skills to potentially start your own company',
        statChanges: { career: 15, wealth: 10, happiness: 15 },
        cost: 3000, // Business courses
      },
      {
        id: 'decline-specialization',
        title: 'Stay Generalist',
        description: 'Continue with your current broad skill set',
        statChanges: { career: 5, happiness: 5 },
        cost: 0,
      },
    ],
  },

  // Teenage Job Decisions (14-17)
  {
    id: 'decision-teenage-job-1',
    title: 'First Teenage Job',
    description: 'You found your first job opportunity! A neighbor needs help with lawn mowing.',
    category: 'career',
    stage: 'childhood',
    minAge: 14,
    maxAge: 16,
    mandatory: false,
    icon: '🌱',
    options: [
      {
        id: 'accept-lawn-mowing',
        title: 'Accept Lawn Mowing Job',
        description: 'Start mowing lawns for $5 per yard - your first job!',
        statChanges: { wealth: 5, happiness: 8, career: 3 },
        cost: 0, // No cost, just income
      },
      {
        id: 'decline-lawn-mowing',
        title: 'Focus on School',
        description: 'Decline to focus on your studies and friends',
        statChanges: { education: 3, happiness: 2 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-teenage-job-2',
    title: 'Better Teenage Job',
    description: 'You found a better job opportunity! A local store needs help on weekends.',
    category: 'career',
    stage: 'childhood',
    minAge: 16,
    maxAge: 17,
    mandatory: false,
    icon: '🏪',
    options: [
      {
        id: 'accept-store-job',
        title: 'Accept Store Job',
        description: 'Work at the store for $8/hour on weekends',
        statChanges: { wealth: 12, happiness: 10, career: 8 },
        cost: 0, // No cost, just income
      },
      {
        id: 'decline-store-job',
        title: 'Keep Current Job',
        description: 'Continue with your current work or focus on school',
        statChanges: { education: 5, happiness: 3 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-first-job',
    title: 'First Real Job Offer',
    description: 'You received your first serious job offer! This could be the start of your career.',
    category: 'career',
    stage: 'early-adulthood',
    minAge: 18,
    maxAge: 22,
    mandatory: false,
    icon: '💼',
    options: [
      {
        id: 'accept-first-job',
        title: 'Accept the Job',
        description: 'Start your career with this opportunity',
        statChanges: { career: 20, wealth: 18, happiness: 12 },
        cost: 0, // No cost, just income
      },
      {
        id: 'decline-first-job',
        title: 'Wait for Better Opportunity',
        description: 'Decline and wait for a better job offer',
        statChanges: { career: 5, happiness: -5 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-part-time-job',
    title: 'Part-Time Job Offer',
    description: 'You found a part-time job opportunity that could help with your expenses while studying.',
    category: 'career',
    stage: 'early-adulthood',
    minAge: 19,
    maxAge: 23,
    mandatory: false,
    icon: '🕐',
    options: [
      {
        id: 'accept-part-time',
        title: 'Accept Part-Time Job',
        description: 'Take the job to earn some money while studying',
        statChanges: { wealth: 15, happiness: 10, career: 8 },
        cost: 0, // No cost, just income
      },
      {
        id: 'decline-part-time',
        title: 'Focus on Studies',
        description: 'Decline to focus entirely on your education',
        statChanges: { education: 5, happiness: 5 },
        cost: 0,
      },
    ],
  },

  {
    id: 'decision-internship',
    title: 'Internship Opportunity',
    description: 'You got an internship offer that could provide valuable experience and some income.',
    category: 'career',
    stage: 'early-adulthood',
    minAge: 20,
    maxAge: 24,
    mandatory: false,
    icon: '📚',
    options: [
      {
        id: 'accept-internship',
        title: 'Accept Internship',
        description: 'Take the internship for experience and income',
        statChanges: { career: 18, wealth: 10, happiness: 12 },
        cost: 0, // No cost, just income
      },
      {
        id: 'decline-internship',
        title: 'Focus on Other Priorities',
        description: 'Decline to focus on other aspects of life',
        statChanges: { happiness: 5 },
        cost: 0,
      },
    ],
  },
];
